import { ActionTitle } from "@/components/actionTitle/ActionTitle";
import { ActionTitleActions } from "@/components/actionTitle/ActionTitleActions";
import { useActiveProject } from "@/features/project/ActiveProject";
import { type Opportunity, disassociateDrawing, getDrawings } from "@repo/api";
import { ListView, ListViewItem, createNewIcon, plusIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  Line,
  PaddedContainer,
  SectionContainer,
  useCustomer,
  useToast,
} from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { STAGE_ACTIONS_SECTION_IDS } from "../project/projectTopSection/stages/config";
import { Drawings } from "./Drawings";
import { useSearchDesign } from "./hooks/search";
import { AddDrawingModal } from "./modals/addDrawings/AddDrawingModal";
import { CreateDrawingModal } from "./modals/addDrawings/CreateDrawingModal";

export const getProjectDrawings = (project?: Opportunity) => {
  if (!project) {
    return undefined;
  }
  const drawings = getDrawings(project.planners); // vpc
  const designs = project.planners.designsInfo; // ipex

  return {
    drawings,
    designs,
    ids: drawings
      .map((d) => d.id)
      .concat(designs?.map((d) => d.designId) ?? []),
  };
};

export const DrawingsSection = ({ editable }: { editable: boolean }) => {
  const { opportunityId } = useParams();
  const { updateToast } = useToast();
  const { opportunity } = useActiveProject();

  const { customer, isValidating: isValidatingCustomer } = useCustomer(
    Number(opportunityId)
  );

  const { data: searchResult } = useSearchDesign(
    undefined,
    isValidatingCustomer ? undefined : customer
  );
  const getHeaderActions = () => {
    return [
      {
        id: "create-drawing-action",
        title: t("action.create_new_drawing"),
        icon: createNewIcon,
        iconOnly: true,
        onClick: () => {
          setIsCreateDrawingModalOpen(true);
          trackAnalytics("create_drawing");
        },
      },
      {
        id: "add-drawing-action",
        text: t("action.add_drawing"),
        title: t("action.add_drawing"),
        icon: plusIcon,
        iconOnly: true,
        onClick: () => {
          setIsAddDrawingModalOpen(true);
          trackAnalytics("open_drawing_search");
        },
      },
    ];
  };

  const [filterAssociated, setFilterAssociated] = useState<boolean>(false);
  const [drawingInLoading, setDrawingInLoading] = useState<string>();
  const [isAddDrawingModalOpen, setIsAddDrawingModalOpen] = useState(false);
  const [isCreateDrawingModalOpen, setIsCreateDrawingModalOpen] =
    useState(false);

  const projectDrawings = getProjectDrawings(opportunity);
  const projectDrawingIds = projectDrawings?.ids ?? [];
  const unassociatedDrawings =
    searchResult?.plannerList
      ?.flatMap((p) => p.designList)
      .filter((res) => !projectDrawings?.ids?.includes(res.id)) ?? [];

  const onDisassociateDrawing = (drawingId: string) => {
    setDrawingInLoading(drawingId);
    disassociateDrawing(opportunityId!, drawingId)
      .then(() => {
        updateToast({
          id: drawingId,
          title: t("prompt.drawing_removed"),
        });
      })
      .catch((e) => {
        updateToast({
          id: drawingId,
          title: t("prompt.drawing_removed_failed"),
          message: getErrorMessage(e),
          type: "error",
        });
      })
      .finally(() => setDrawingInLoading(undefined));
  };

  if (!opportunity) {
    return null;
  }

  return (
    <PaddedContainer>
      <SectionContainer id={STAGE_ACTIONS_SECTION_IDS.DRAWINGS_SECTION}>
        <ActionTitle
          title={`${
            projectDrawingIds.length > 1
              ? t("label.drawings")
              : t("label.drawing")
          } (${projectDrawingIds.length})`}
          action={
            editable && <ActionTitleActions actions={getHeaderActions()} />
          }
        />
        <Drawings
          editable={editable}
          drawings={projectDrawings?.drawings}
          onDisassociate={onDisassociateDrawing}
          drawingInLoading={drawingInLoading}
          designs={projectDrawings?.designs}
        />
      </SectionContainer>
      {editable && unassociatedDrawings.length > 0 && (
        <ListView id="disassociated-drawing-list" size="small">
          <Line />
          <ListViewItem
            control="navigational"
            onClick={() => {
              setFilterAssociated(true);
              setIsAddDrawingModalOpen(true);
              trackAnalytics("show_unassociated_drawings");
            }}
            title={
              unassociatedDrawings.length > 1
                ? t("prompt.found_multiple_drawings_not_associated", {
                    total: unassociatedDrawings.length,
                  })
                : t("prompt.found_one_drawing_not_associated")
            }
          />
        </ListView>
      )}

      <AddDrawingModal
        isOpen={isAddDrawingModalOpen}
        closeModal={() => {
          setIsAddDrawingModalOpen(false);
          setFilterAssociated(false);
        }}
        filterAssociated={filterAssociated}
        opportunityId={opportunityId!}
        existingDrawingIds={projectDrawingIds}
        customer={customer}
      />

      <CreateDrawingModal
        isOpen={isCreateDrawingModalOpen}
        closeModal={() => {
          setIsCreateDrawingModalOpen(false);
          setFilterAssociated(false);
        }}
        plannerList={searchResult?.plannerList ?? []}
      />
    </PaddedContainer>
  );
};
